import React, { useState, useMemo } from 'react';
import useContainerMobile from '../../hooks/useContainerMobile';
import BacktestStats from './BacktestStats';
import BacktestChart from './BacktestChart';
import EquityChart from './EquityChart';
import TradeLog from './TradeLog';
import PlaybackControls from './PlaybackControls';
import BacktestOrderBook from './BacktestOrderBook';

const cardClass = 'rounded-xl border border-white/8 bg-card/40 backdrop-blur-md shadow-sm';

/* ── Tab bar ────────────────────────────────────────────── */
function TabBar({ activeTab, setActiveTab }) {
  const tabs = [
    { id: 'orders', label: 'Order Book' },
    { id: 'trades', label: 'Trade Log' },
  ];
  return (
    <div className="flex border-b border-border flex-shrink-0">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => setActiveTab(t.id)}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === t.id
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ── Empty placeholder ── */
function EmptyPanel({ message, icon }) {
  return (
    <div className="flex items-center justify-center h-full text-center">
      <div>
        {icon && <div className="text-3xl mb-2 opacity-30">{icon}</div>}
        <p className="text-xs text-muted-foreground/50">{message}</p>
      </div>
    </div>
  );
}

export default function BacktestResults({ results, initialCapital, strategy, overlays }) {
  const { containerRef, isMobile } = useContainerMobile();
  const [playbackIndex, setPlaybackIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');

  const strategyType = strategy?.type;
  const parameters = typeof strategy?.parameters === 'string'
    ? JSON.parse(strategy.parameters)
    : strategy?.parameters;

  const rebalanceEvents = results?.rebalanceEvents || [];

  // Compute grid levels — when rebalancing, use the bounds active at the current playback position
  const gridLevels = useMemo(() => {
    if (strategyType !== 'grid' || !parameters) return [];
    const count = parseInt(parameters.gridLevels, 10) || 10;
    if (count < 2) return [];

    let lower = parseFloat(parameters.lowerBound);
    let upper = parseFloat(parameters.upperBound);

    // If rebalancing occurred, use bounds from the most recent rebalance at or before playbackIndex
    if (rebalanceEvents.length > 0 && playbackIndex != null) {
      for (let r = rebalanceEvents.length - 1; r >= 0; r--) {
        if (rebalanceEvents[r].barIndex <= playbackIndex) {
          lower = rebalanceEvents[r].lowerBound;
          upper = rebalanceEvents[r].upperBound;
          break;
        }
      }
    } else if (rebalanceEvents.length > 0 && playbackIndex == null) {
      // "All bars" mode — use final rebalance bounds
      const last = rebalanceEvents[rebalanceEvents.length - 1];
      lower = last.lowerBound;
      upper = last.upperBound;
    }

    if (isNaN(lower) || isNaN(upper) || lower >= upper) return [];
    const step = (upper - lower) / (count - 1);
    const levels = [];
    for (let i = 0; i < count; i++) levels.push(parseFloat((lower + step * i).toFixed(2)));
    return levels;
  }, [strategyType, parameters, rebalanceEvents, playbackIndex]);

  const hasResults = !!results;
  const trades = results?.trades;
  const equityCurve = results?.equityCurve;
  const snapshots = results?.snapshots;
  const metrics = results?.metrics;
  const drawdownCurve = results?.drawdownCurve;
  const bars = results?.bars;

  /* ── Shared sub-components ── */
  const statsBar = (
    <div className={`${cardClass} px-4 py-3 flex-shrink-0`}>
      {hasResults ? (
        <BacktestStats metrics={metrics} initialCapital={initialCapital} symbol={parameters?.symbol} />
      ) : (
        <div className="flex items-center gap-3 text-muted-foreground/40 text-xs py-1 flex-wrap">
          <span>Return —</span><span>Equity —</span><span>Trades —</span>
          <span>Win Rate —</span><span>Sharpe —</span><span>Max DD —</span><span>Fees —</span>
        </div>
      )}
    </div>
  );

  const chartPanel = hasResults ? (
    <BacktestChart
      bars={bars}
      trades={trades}
      gridLevels={gridLevels}
      playbackIndex={playbackIndex}
      strategyType={strategyType}
      overlays={overlays}
    />
  ) : (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <div className="text-5xl mb-3 opacity-20">📊</div>
        <p className="text-muted-foreground/50 text-sm">Select a strategy and run a backtest</p>
      </div>
    </div>
  );

  const playbackBar = hasResults && bars?.length > 0 ? (
    <PlaybackControls
      totalBars={bars.length}
      playbackIndex={playbackIndex}
      onIndexChange={setPlaybackIndex}
      bars={bars}
    />
  ) : (
    <div className="flex items-center gap-3 px-3 py-2 bg-card/30 border border-border/30 rounded-lg opacity-40">
      <span className="text-xs text-muted-foreground/50">◁</span>
      <span className="text-xs text-muted-foreground/50">▶</span>
      <span className="text-xs text-muted-foreground/50">▷</span>
      <div className="flex-1 h-1.5 bg-border/20 rounded-full" />
      <span className="text-xs text-muted-foreground/40">—</span>
    </div>
  );

  const equityPanel = (
    <div className={`${cardClass} p-3`} style={{ height: 200 }}>
      {hasResults ? (
        <EquityChart equityCurve={equityCurve} drawdownCurve={drawdownCurve} bars={bars} initialCapital={initialCapital} />
      ) : (
        <EmptyPanel icon="📈" message="Equity curve" />
      )}
    </div>
  );

  const orderBookPanel = (
    <div className={`${cardClass} flex flex-col flex-1 min-h-0 overflow-hidden`}>
      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {hasResults ? (
          <>
            {activeTab === 'orders' && <BacktestOrderBook trades={trades} snapshots={snapshots} playbackIndex={playbackIndex} />}
            {activeTab === 'trades' && <TradeLog trades={trades} bars={bars} playbackIndex={playbackIndex} />}
          </>
        ) : (
          <EmptyPanel icon={activeTab === 'orders' ? '📋' : '📜'} message={activeTab === 'orders' ? 'No orders yet' : 'No trades yet'} />
        )}
      </div>
    </div>
  );

  /* ══════════════════ MOBILE LAYOUT ══════════════════ */
  if (isMobile) {
    return (
      <div ref={containerRef} className="flex flex-col gap-3 overflow-y-auto h-full">
        {/* Stats */}
        {statsBar}

        {/* Chart */}
        <div className={`${cardClass} p-3 flex flex-col min-h-[50vh]`}>
          <div className="flex-1 min-h-0">{chartPanel}</div>
        </div>

        {/* Playback */}
        {playbackBar}

        {/* Equity */}
        {equityPanel}

        {/* Order Book / Trade Log */}
        <div className={`${cardClass} flex flex-col min-h-[40vh]`}>
          <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {hasResults ? (
              <>
                {activeTab === 'orders' && <BacktestOrderBook trades={trades} snapshots={snapshots} playbackIndex={playbackIndex} />}
                {activeTab === 'trades' && <TradeLog trades={trades} bars={bars} playbackIndex={playbackIndex} />}
              </>
            ) : (
              <EmptyPanel icon={activeTab === 'orders' ? '📋' : '📜'} message={activeTab === 'orders' ? 'No orders yet' : 'No trades yet'} />
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════ DESKTOP LAYOUT ══════════════════ */
  return (
    <div ref={containerRef} className="grid grid-cols-8 gap-3 h-full min-h-0"
         style={{ gridTemplateRows: 'auto 1fr auto' }}>

      {/* Stats bar — 6 cols */}
      <div className="col-span-6 col-start-1">{statsBar}</div>

      {/* Right sidebar — 2 cols, full height */}
      <div className="col-span-2 row-span-3 row-start-1 col-start-7 flex flex-col min-h-0 gap-3">
        {equityPanel}
        {orderBookPanel}
      </div>

      {/* Chart — 6 cols */}
      <div className={`col-span-6 col-start-1 ${cardClass} p-3 flex flex-col min-h-0 overflow-hidden`}>
        <div className="flex-1 min-h-0">{chartPanel}</div>
      </div>

      {/* Playback — 6 cols */}
      <div className="col-span-6 col-start-1 flex-shrink-0">{playbackBar}</div>
    </div>
  );
}
